import type {
  BoardFloorPlanData,
  FloorPlanFootprint,
  FloorPlanItem,
  FloorPlanOpening,
  FloorPlanUnderlay,
  FloorPlanUnderlayCrop,
  FloorPlanWall,
} from '../../../types';
import { formatLength } from '../../../utils/measurementMath';

export const DEFAULT_FOOTPRINT_WIDTH_IN = 36 * 12;
export const DEFAULT_FOOTPRINT_DEPTH_IN = 24 * 12;
export const DEFAULT_EXTERIOR_THICKNESS_IN = 6;
export const DEFAULT_INTERIOR_THICKNESS_IN = 4;
export const GRID_MINOR_IN = 6;
export const GRID_MAJOR_IN = 12;
export const MIN_WALL_LENGTH_IN = 12;
export const WALL_HIT_IN = 10;
export const DEFAULT_UNDERLAY_OPACITY = 0.4;
export const MIN_UNDERLAY_WIDTH_IN = 24;
export const MIN_UNDERLAY_CROP_FRAC = 0.08;
export const MIN_UNDERLAY_VISIBLE_IN = 24;

export type FloorPlanCropEdge = 'left' | 'right' | 'top' | 'bottom';

export const EXTERIOR_WALL_IDS = {
  n: 'fp-n',
  e: 'fp-e',
  s: 'fp-s',
  w: 'fp-w',
} as const;

export type FloorPlanViewBox = { x: number; y: number; w: number; h: number };

export function genFloorPlanId(prefix: string): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function isExteriorWallId(id: string): boolean {
  return (
    id === EXTERIOR_WALL_IDS.n ||
    id === EXTERIOR_WALL_IDS.e ||
    id === EXTERIOR_WALL_IDS.s ||
    id === EXTERIOR_WALL_IDS.w
  );
}

/**
 * Parse a typed dimension. A bare number uses `bareUnit` (feet for the
 * footprint, inches for furniture / openings). `'`, `"`, and mixed `36' 6"` work.
 */
export function parseDimensionToInches(raw: string, bareUnit: 'ft' | 'in' = 'ft'): number | null {
  const text = raw.trim().toLowerCase().replace(/–|—/g, '-');
  if (!text) return null;

  const feetInch = text.match(/^(-?\d+(?:\.\d+)?)\s*'\s*-?\s*(\d+(?:\.\d+)?)(?:\s*(\d+)\s*\/\s*(\d+))?\s*"?$/);
  if (feetInch) {
    const feet = Number(feetInch[1]);
    let inches = Number(feetInch[2]);
    if (feetInch[3] && feetInch[4]) {
      const den = Number(feetInch[4]);
      if (den) inches += Number(feetInch[3]) / den;
    }
    if (!Number.isFinite(feet) || !Number.isFinite(inches)) return null;
    return feet * 12 + inches;
  }

  const feetOnly = text.match(/^(-?\d+(?:\.\d+)?)\s*'$/);
  if (feetOnly) {
    const feet = Number(feetOnly[1]);
    return Number.isFinite(feet) ? feet * 12 : null;
  }

  const inchesOnly = text.match(/^(-?\d+(?:\.\d+)?)\s*"$/);
  if (inchesOnly) {
    const inches = Number(inchesOnly[1]);
    return Number.isFinite(inches) ? inches : null;
  }

  const bare = Number(text);
  if (!Number.isFinite(bare)) return null;
  return bareUnit === 'ft' ? bare * 12 : bare;
}

export function formatInchesFtIn(inches: number): string {
  return formatLength(inches, 'in', 'ft-in');
}

export function snapInches(value: number, grid = GRID_MINOR_IN): number {
  if (grid <= 0) return value;
  return Math.round(value / grid) * grid;
}

export function wallHandleSize(viewBox?: FloorPlanViewBox, selected = false): number {
  const span = viewBox?.w || 400;
  const raw = span * 0.004;
  return selected ? Math.min(3, Math.max(2, raw)) : Math.min(2.4, Math.max(1.6, raw));
}

export function wallHandleHitInches(viewBox?: FloorPlanViewBox): number {
  return wallHandleSize(viewBox, true) * 1.75;
}

export function dimensionFontInches(viewBox?: FloorPlanViewBox, pxPerIn?: number): number {
  if (pxPerIn && pxPerIn > 0) {
    return Math.min(22, Math.max(1.5, 12 / pxPerIn));
  }
  const span = viewBox?.w || 400;
  return Math.min(18, Math.max(2.2, span * 0.015));
}

export function wallDimensionAnchor(
  wall: Pick<FloorPlanWall, 'x1' | 'y1' | 'x2' | 'y2' | 'thicknessIn'>,
  viewBox?: FloorPlanViewBox,
  pxPerIn?: number,
): { x: number; y: number } {
  const { ux, uy, length } = wallUnit(wall);
  const font = dimensionFontInches(viewBox, pxPerIn);
  const offset = (wall.thicknessIn || 4) / 2 + font * 0.85;
  return {
    x: (wall.x1 + wall.x2) / 2 - (length < 1e-6 ? 0 : uy * offset),
    y: (wall.y1 + wall.y2) / 2 + (length < 1e-6 ? 0 : ux * offset),
  };
}

export function dimensionLabelBox(
  wall: Pick<FloorPlanWall, 'x1' | 'y1' | 'x2' | 'y2' | 'thicknessIn'>,
  viewBox?: FloorPlanViewBox,
  pxPerIn?: number,
): { x: number; y: number; w: number; h: number; font: number; cx: number; cy: number } {
  const font = dimensionFontInches(viewBox, pxPerIn);
  const { x: cx, y: cy } = wallDimensionAnchor(wall, viewBox, pxPerIn);
  const label = formatInchesFtIn(wallLength(wall));
  const w = Math.max(font * 2.4, label.length * font * 0.62) + font * 0.35;
  const h = font * 1.45;
  return { x: cx - w / 2, y: cy - h / 2, w, h, font, cx, cy };
}

export function hitTestDimension(
  walls: FloorPlanWall[],
  x: number,
  y: number,
  viewBox?: FloorPlanViewBox,
  pxPerIn?: number,
): FloorPlanWall | null {
  let best: FloorPlanWall | null = null;
  let bestArea = Infinity;
  for (const wall of walls) {
    if (wallLength(wall) < 8) continue;
    const box = dimensionLabelBox(wall, viewBox, pxPerIn);
    if (x < box.x || x > box.x + box.w || y < box.y || y > box.y + box.h) continue;
    const area = box.w * box.h;
    if (area < bestArea) {
      best = wall;
      bestArea = area;
    }
  }
  return best;
}

export function wallLength(wall: Pick<FloorPlanWall, 'x1' | 'y1' | 'x2' | 'y2'>): number {
  return Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
}

export function wallUnit(wall: Pick<FloorPlanWall, 'x1' | 'y1' | 'x2' | 'y2'>): {
  ux: number;
  uy: number;
  length: number;
} {
  const length = wallLength(wall);
  if (length < 1e-6) return { ux: 1, uy: 0, length: 0 };
  return { ux: (wall.x2 - wall.x1) / length, uy: (wall.y2 - wall.y1) / length, length };
}

/** Snap the drag end to the nearer axis so walls stay orthogonal. */
export function orthogonalizeEnd(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): { x2: number; y2: number } {
  if (Math.abs(x2 - x1) >= Math.abs(y2 - y1)) {
    return { x2, y2: y1 };
  }
  return { x2: x1, y2 };
}

export function pointAlongWall(
  wall: Pick<FloorPlanWall, 'x1' | 'y1' | 'x2' | 'y2'>,
  offsetIn: number,
): { x: number; y: number } {
  const { ux, uy } = wallUnit(wall);
  return { x: wall.x1 + ux * offsetIn, y: wall.y1 + uy * offsetIn };
}

export function clampOpeningOnWall(
  wall: Pick<FloorPlanWall, 'x1' | 'y1' | 'x2' | 'y2'>,
  opening: Pick<FloorPlanOpening, 'offsetIn' | 'widthIn'>,
): { offsetIn: number; widthIn: number } {
  const length = wallLength(wall);
  const widthIn = Math.min(Math.max(opening.widthIn, 12), Math.max(12, length - 2));
  const offsetIn = Math.min(Math.max(opening.offsetIn, 0), Math.max(0, length - widthIn));
  return { offsetIn, widthIn };
}

export function wallPerp(wall: Pick<FloorPlanWall, 'x1' | 'y1' | 'x2' | 'y2'>): { x: number; y: number } {
  const { ux, uy } = wallUnit(wall);
  return { x: -uy, y: ux };
}

export function projectOnWall(
  wall: Pick<FloorPlanWall, 'x1' | 'y1' | 'x2' | 'y2'>,
  x: number,
  y: number,
): { offsetIn: number; dist: number; rawOffset: number; signedDist: number } {
  const { ux, uy, length } = wallUnit(wall);
  const perp = wallPerp(wall);
  const rawOffset = length < 1e-6 ? 0 : (x - wall.x1) * ux + (y - wall.y1) * uy;
  const signedDist = (x - wall.x1) * perp.x + (y - wall.y1) * perp.y;
  if (length < 1e-6) {
    return { offsetIn: 0, rawOffset: 0, dist: Math.hypot(x - wall.x1, y - wall.y1), signedDist };
  }
  const clamped = Math.min(Math.max(rawOffset, 0), length);
  const pt = pointAlongWall(wall, clamped);
  return { offsetIn: clamped, rawOffset, dist: Math.hypot(x - pt.x, y - pt.y), signedDist };
}

export function garageDoorLayout(
  wall: FloorPlanWall,
  opening: Pick<FloorPlanOpening, 'widthIn' | 'flip'>,
): {
  half: number;
  face: number;
  jambInset: number;
  panels: number;
  inward: { x: number; y: number };
  out: { x: number; y: number };
} {
  const half = (wall.thicknessIn || 6) / 2;
  const perp = wallPerp(wall);
  const sign = opening.flip ? 1 : -1;
  const out = { x: perp.x * sign, y: perp.y * sign };
  return {
    half,
    face: Math.max(5, half + 4),
    jambInset: Math.min(3, Math.max(1.5, opening.widthIn * 0.03)),
    panels: Math.max(3, Math.min(7, Math.round(opening.widthIn / 22))),
    inward: { x: -out.x, y: -out.y },
    out,
  };
}

export function nearestWall(
  walls: FloorPlanWall[],
  x: number,
  y: number,
  maxDist = WALL_HIT_IN,
): FloorPlanWall | null {
  let best: FloorPlanWall | null = null;
  let bestDist = maxDist;
  for (const wall of walls) {
    const { dist } = projectOnWall(wall, x, y);
    if (dist <= bestDist) {
      best = wall;
      bestDist = dist;
    }
  }
  return best;
}

export function exteriorWallsFromFootprint(
  widthIn: number,
  depthIn: number,
  thicknessIn = DEFAULT_EXTERIOR_THICKNESS_IN,
): FloorPlanWall[] {
  return [
    { id: EXTERIOR_WALL_IDS.n, x1: 0, y1: 0, x2: widthIn, y2: 0, thicknessIn },
    { id: EXTERIOR_WALL_IDS.e, x1: widthIn, y1: 0, x2: widthIn, y2: depthIn, thicknessIn },
    { id: EXTERIOR_WALL_IDS.s, x1: widthIn, y1: depthIn, x2: 0, y2: depthIn, thicknessIn },
    { id: EXTERIOR_WALL_IDS.w, x1: 0, y1: depthIn, x2: 0, y2: 0, thicknessIn },
  ];
}

export function createDefaultFloorPlan(title = 'Floor plan'): BoardFloorPlanData {
  return {
    title,
    footprint: { widthIn: DEFAULT_FOOTPRINT_WIDTH_IN, depthIn: DEFAULT_FOOTPRINT_DEPTH_IN },
    walls: [],
    openings: [],
    items: [],
    underlays: [],
    constraints: [],
  };
}

export function wallsFromRect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  thicknessIn = DEFAULT_EXTERIOR_THICKNESS_IN,
): FloorPlanWall[] {
  const left = Math.min(x1, x2);
  const right = Math.max(x1, x2);
  const top = Math.min(y1, y2);
  const bottom = Math.max(y1, y2);
  if (right - left < MIN_WALL_LENGTH_IN || bottom - top < MIN_WALL_LENGTH_IN) return [];
  return [
    { id: genFloorPlanId('wall'), x1: left, y1: top, x2: right, y2: top, thicknessIn },
    { id: genFloorPlanId('wall'), x1: right, y1: top, x2: right, y2: bottom, thicknessIn },
    { id: genFloorPlanId('wall'), x1: right, y1: bottom, x2: left, y2: bottom, thicknessIn },
    { id: genFloorPlanId('wall'), x1: left, y1: bottom, x2: left, y2: top, thicknessIn },
  ];
}

export const CARDINAL_ANGLES = [0, 90, 180, 270] as const;
export const CARDINAL_SNAP_DEG = 5;

export function normalizeDegrees(deg: number): number {
  if (!Number.isFinite(deg)) return 0;
  const n = deg % 360;
  return n < 0 ? n + 360 : n;
}

export function underlayRotation(underlay: Pick<FloorPlanUnderlay, 'rotation'>): number {
  return normalizeDegrees(underlay.rotation || 0);
}

export function snapRotationToCardinal(deg: number, threshold = CARDINAL_SNAP_DEG): number {
  const n = normalizeDegrees(deg);
  let best = n;
  let bestDist = threshold + 1;
  for (let i = 0; i < CARDINAL_ANGLES.length; i += 1) {
    const card = CARDINAL_ANGLES[i];
    const dist = Math.min(Math.abs(n - card), 360 - Math.abs(n - card));
    if (dist <= threshold && dist < bestDist) {
      best = card;
      bestDist = dist;
    }
  }
  if (n >= 360 - threshold) return 0;
  return best;
}

export function rotatedRectSize(widthIn: number, heightIn: number, deg: number): { w: number; h: number } {
  const n = normalizeDegrees(deg);
  if (n < 1e-6 || Math.abs(n - 180) < 1e-6) return { w: widthIn, h: heightIn };
  if (Math.abs(n - 90) < 1e-6 || Math.abs(n - 270) < 1e-6) return { w: heightIn, h: widthIn };
  const rad = (n * Math.PI) / 180;
  const c = Math.abs(Math.cos(rad));
  const s = Math.abs(Math.sin(rad));
  return { w: widthIn * c + heightIn * s, h: widthIn * s + heightIn * c };
}

export function underlayAabb(underlay: FloorPlanUnderlay): { x: number; y: number; w: number; h: number } {
  const size = rotatedRectSize(underlay.widthIn, underlay.heightIn, underlayRotation(underlay));
  return {
    x: underlay.x,
    y: underlay.y,
    w: size.w,
    h: size.h,
  };
}

export function underlayIsHidden(underlay: FloorPlanUnderlay | null | undefined): boolean {
  return !!underlay?.hidden;
}

export function setUnderlayHidden(underlay: FloorPlanUnderlay, hidden: boolean): FloorPlanUnderlay {
  return { ...underlay, hidden };
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function minVisibleFrac(fullSpan: number): number {
  if (fullSpan <= 0) return MIN_UNDERLAY_CROP_FRAC;
  return Math.min(0.85, Math.max(MIN_UNDERLAY_CROP_FRAC, MIN_UNDERLAY_VISIBLE_IN / fullSpan));
}

export function underlayCrop(underlay: FloorPlanUnderlay): FloorPlanUnderlayCrop {
  const crop = underlay.crop;
  if (!crop) return { left: 0, top: 0, right: 0, bottom: 0 };
  return {
    left: clamp01(crop.left || 0),
    top: clamp01(crop.top || 0),
    right: clamp01(crop.right || 0),
    bottom: clamp01(crop.bottom || 0),
  };
}

export function underlayIsCropped(underlay: FloorPlanUnderlay): boolean {
  const crop = underlayCrop(underlay);
  return crop.left > 1e-4 || crop.top > 1e-4 || crop.right > 1e-4 || crop.bottom > 1e-4;
}

export function resetUnderlayCrop(underlay: FloorPlanUnderlay): FloorPlanUnderlay {
  if (underlay.locked || !underlay.crop) return underlay;
  const next = { ...underlay };
  delete next.crop;
  return next;
}

export function underlayIsLocked(underlay: FloorPlanUnderlay | null | undefined): boolean {
  return !!underlay?.locked;
}

export function setUnderlayLocked(underlay: FloorPlanUnderlay, locked: boolean): FloorPlanUnderlay {
  return { ...underlay, locked };
}

export function rotateUnderlayCrop(crop: FloorPlanUnderlayCrop): FloorPlanUnderlayCrop {
  return {
    left: crop.bottom,
    top: crop.left,
    right: crop.top,
    bottom: crop.right,
  };
}

export function underlayVisibleAabb(underlay: FloorPlanUnderlay): { x: number; y: number; w: number; h: number } {
  const full = underlayAabb(underlay);
  const crop = underlayCrop(underlay);
  return {
    x: full.x + full.w * crop.left,
    y: full.y + full.h * crop.top,
    w: Math.max(1e-3, full.w * Math.max(0, 1 - crop.left - crop.right)),
    h: Math.max(1e-3, full.h * Math.max(0, 1 - crop.top - crop.bottom)),
  };
}

export function cropUnderlayEdge(
  underlay: FloorPlanUnderlay,
  edge: FloorPlanCropEdge,
  world: number,
): FloorPlanUnderlay {
  if (underlay.locked) return underlay;
  const full = underlayAabb(underlay);
  const crop = underlayCrop(underlay);
  const next = { ...crop };
  if (edge === 'left' || edge === 'right') {
    const minVis = minVisibleFrac(full.w);
    if (edge === 'left') {
      next.left = Math.min(1 - crop.right - minVis, Math.max(0, (world - full.x) / Math.max(full.w, 1e-3)));
    } else {
      next.right = Math.min(1 - crop.left - minVis, Math.max(0, (full.x + full.w - world) / Math.max(full.w, 1e-3)));
    }
  } else {
    const minVis = minVisibleFrac(full.h);
    if (edge === 'top') {
      next.top = Math.min(1 - crop.bottom - minVis, Math.max(0, (world - full.y) / Math.max(full.h, 1e-3)));
    } else {
      next.bottom = Math.min(1 - crop.top - minVis, Math.max(0, (full.y + full.h - world) / Math.max(full.h, 1e-3)));
    }
  }
  return { ...underlay, crop: next };
}

export function scaleUnderlayFromVisibleWidth(underlay: FloorPlanUnderlay, visibleWidthIn: number): FloorPlanUnderlay {
  if (underlay.locked) return underlay;
  const crop = underlayCrop(underlay);
  const visFracW = Math.max(1e-3, 1 - crop.left - crop.right);
  const vis = underlayVisibleAabb(underlay);
  const aspect = vis.h > 0 ? vis.w / vis.h : 1;
  const nextVisW = Math.max(MIN_UNDERLAY_WIDTH_IN * visFracW, visibleWidthIn);
  const nextVisH = nextVisW / aspect;
  const nextFullW = nextVisW / visFracW;
  const nextFullH = nextVisH / Math.max(1e-3, 1 - crop.top - crop.bottom);
  const rot = underlayRotation(underlay);
  const rad = (rot * Math.PI) / 180;
  const c = Math.abs(Math.cos(rad));
  const s = Math.abs(Math.sin(rad));
  const imageAspect = underlay.widthIn > 0 ? underlay.heightIn / underlay.widthIn : 1;
  const nextWidthIn = nextFullW / Math.max(c + imageAspect * s, 1e-3);
  const nextHeightIn = nextWidthIn * imageAspect;
  return {
    ...underlay,
    x: vis.x - nextFullW * crop.left,
    y: vis.y - nextFullH * crop.top,
    widthIn: nextWidthIn,
    heightIn: nextHeightIn,
  };
}

export function underlayCropHandleRects(
  underlay: FloorPlanUnderlay,
  viewBox?: FloorPlanViewBox,
): Array<{ edge: FloorPlanCropEdge; x: number; y: number; w: number; h: number }> {
  const vis = underlayVisibleAabb(underlay);
  const handle = viewBox ? Math.max(6, viewBox.w * 0.012) : 10;
  const thick = Math.max(5, handle * 0.55);
  const long = Math.max(16, handle * 2.4);
  const midX = vis.x + vis.w / 2;
  const midY = vis.y + vis.h / 2;
  return [
    { edge: 'left', x: vis.x - thick / 2, y: midY - long / 2, w: thick, h: long },
    { edge: 'right', x: vis.x + vis.w - thick / 2, y: midY - long / 2, w: thick, h: long },
    { edge: 'top', x: midX - long / 2, y: vis.y - thick / 2, w: long, h: thick },
    { edge: 'bottom', x: midX - long / 2, y: vis.y + vis.h - thick / 2, w: long, h: thick },
  ];
}

export function hitTestUnderlayCropEdge(
  underlay: FloorPlanUnderlay,
  x: number,
  y: number,
  viewBox: FloorPlanViewBox,
): FloorPlanCropEdge | null {
  const slop = Math.max(3, viewBox.w * 0.006);
  const handles = underlayCropHandleRects(underlay, viewBox);
  for (let i = 0; i < handles.length; i += 1) {
    const handle = handles[i];
    if (
      x >= handle.x - slop &&
      x <= handle.x + handle.w + slop &&
      y >= handle.y - slop &&
      y <= handle.y + handle.h + slop
    ) {
      return handle.edge;
    }
  }
  return null;
}

export function hitsUnderlayScaleHandle(
  underlay: FloorPlanUnderlay,
  x: number,
  y: number,
  viewBox: FloorPlanViewBox,
): boolean {
  const box = underlayVisibleAabb(underlay);
  const handle = Math.max(8, viewBox.w * 0.018);
  return Math.abs(x - (box.x + box.w)) <= handle && Math.abs(y - (box.y + box.h)) <= handle;
}

export function underlayRotateHandle(
  underlay: FloorPlanUnderlay,
  viewBox?: FloorPlanViewBox,
): { x: number; y: number; r: number } {
  const box = underlayVisibleAabb(underlay);
  const r = viewBox ? Math.max(5, viewBox.w * 0.01) : 8;
  return {
    x: box.x + box.w / 2,
    y: box.y - Math.max(12, (viewBox?.w || 400) * 0.022),
    r,
  };
}

export function hitsUnderlayRotateHandle(
  underlay: FloorPlanUnderlay,
  x: number,
  y: number,
  viewBox: FloorPlanViewBox,
): boolean {
  const handle = underlayRotateHandle(underlay, viewBox);
  return Math.hypot(x - handle.x, y - handle.y) <= handle.r * 1.85;
}

export function angleDegFromCenter(cx: number, cy: number, x: number, y: number): number {
  return (Math.atan2(y - cy, x - cx) * 180) / Math.PI;
}

export function createUnderlay(opts: {
  url: string;
  storagePath?: string;
  label?: string;
  naturalWidth?: number;
  naturalHeight?: number;
  footprint: FloorPlanFootprint;
}): FloorPlanUnderlay {
  const nw = opts.naturalWidth || 1600;
  const nh = opts.naturalHeight || 1200;
  const aspect = nw > 0 ? nh / nw : 0.75;
  const widthIn = Math.max(120, Math.min(opts.footprint.widthIn * 0.85, 480));
  const heightIn = widthIn * aspect;
  return {
    id: genFloorPlanId('trace'),
    url: opts.url,
    storagePath: opts.storagePath,
    label: opts.label,
    x: Math.max(0, (opts.footprint.widthIn - widthIn) / 2),
    y: Math.max(0, (opts.footprint.depthIn - heightIn) / 2),
    widthIn,
    heightIn,
    opacity: DEFAULT_UNDERLAY_OPACITY,
    rotation: 0,
    naturalWidth: opts.naturalWidth,
    naturalHeight: opts.naturalHeight,
  };
}

export function scaleUnderlay(underlay: FloorPlanUnderlay, widthIn: number): FloorPlanUnderlay {
  if (underlay.locked) return underlay;
  const aspect = underlay.widthIn > 0 ? underlay.heightIn / underlay.widthIn : 1;
  const nextW = Math.max(MIN_UNDERLAY_WIDTH_IN, widthIn);
  return { ...underlay, widthIn: nextW, heightIn: nextW * aspect };
}

/** Scale a trace so the world distance between two picked points becomes `realLengthIn`. Point A stays put. */
export function scaleUnderlayFromPoints(
  underlay: FloorPlanUnderlay,
  a: { x: number; y: number },
  b: { x: number; y: number },
  realLengthIn: number,
): FloorPlanUnderlay {
  if (underlay.locked) return underlay;
  const measured = Math.hypot(b.x - a.x, b.y - a.y);
  if (measured < 1 || !Number.isFinite(realLengthIn) || realLengthIn < 1) return underlay;
  const factor = realLengthIn / measured;
  const nextW = Math.max(MIN_UNDERLAY_WIDTH_IN, underlay.widthIn * factor);
  const used = underlay.widthIn > 0 ? nextW / underlay.widthIn : factor;
  return {
    ...underlay,
    widthIn: nextW,
    heightIn: underlay.heightIn * used,
    x: a.x - (a.x - underlay.x) * used,
    y: a.y - (a.y - underlay.y) * used,
  };
}

export function setUnderlayRotation(
  underlay: FloorPlanUnderlay,
  deg: number,
  snap = false,
): FloorPlanUnderlay {
  if (underlay.locked) return underlay;
  const nextRot = snap ? snapRotationToCardinal(deg) : normalizeDegrees(deg);
  const old = underlayAabb(underlay);
  const next = { ...underlay, rotation: nextRot };
  const neu = underlayAabb(next);
  return {
    ...next,
    x: old.x + old.w / 2 - neu.w / 2,
    y: old.y + old.h / 2 - neu.h / 2,
  };
}

export function rotateUnderlay(underlay: FloorPlanUnderlay): FloorPlanUnderlay {
  if (underlay.locked) return underlay;
  const turned = setUnderlayRotation(underlay, underlayRotation(underlay) + 90);
  if (!underlayIsCropped(underlay)) return turned;
  return { ...turned, crop: rotateUnderlayCrop(underlayCrop(underlay)) };
}

export function underlayRenderStyles(
  underlay: FloorPlanUnderlay,
  viewBox: FloorPlanViewBox,
): {
  clip: {
    left: string;
    top: string;
    width: string;
    height: string;
    opacity: number;
  };
  image: {
    left: string;
    top: string;
    width: string;
    height: string;
    transform?: string;
    transformOrigin: string;
  };
} {
  const vis = underlayVisibleAabb(underlay);
  const full = underlayAabb(underlay);
  const cx = full.x + full.w / 2;
  const cy = full.y + full.h / 2;
  const imgLeft = cx - underlay.widthIn / 2 - vis.x;
  const imgTop = cy - underlay.heightIn / 2 - vis.y;
  const rot = underlay.rotation || 0;
  return {
    clip: {
      left: `${((vis.x - viewBox.x) / viewBox.w) * 100}%`,
      top: `${((vis.y - viewBox.y) / viewBox.h) * 100}%`,
      width: `${(vis.w / viewBox.w) * 100}%`,
      height: `${(vis.h / viewBox.h) * 100}%`,
      opacity: underlay.opacity,
    },
    image: {
      left: `${(imgLeft / vis.w) * 100}%`,
      top: `${(imgTop / vis.h) * 100}%`,
      width: `${(underlay.widthIn / vis.w) * 100}%`,
      height: `${(underlay.heightIn / vis.h) * 100}%`,
      transform: rot ? `rotate(${rot}deg)` : undefined,
      transformOrigin: 'center center',
    },
  };
}

export function hitTestUnderlay(
  underlays: FloorPlanUnderlay[] | undefined,
  x: number,
  y: number,
): FloorPlanUnderlay | null {
  const list = underlays || [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (underlayIsHidden(list[i])) continue;
    const box = underlayVisibleAabb(list[i]);
    if (x >= box.x && x <= box.x + box.w && y >= box.y && y <= box.y + box.h) return list[i];
  }
  return null;
}

export function contentBounds(
  data: Pick<BoardFloorPlanData, 'walls' | 'items' | 'underlays' | 'measures'>,
): { minX: number; minY: number; maxX: number; maxY: number; widthIn: number; depthIn: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const consider = (x: number, y: number) => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  };
  for (const wall of data.walls || []) {
    consider(wall.x1, wall.y1);
    consider(wall.x2, wall.y2);
  }
  for (const item of data.items || []) {
    const box = itemAabb(item);
    consider(box.x, box.y);
    consider(box.x + box.w, box.y + box.h);
  }
  for (const underlay of data.underlays || []) {
    if (underlayIsHidden(underlay)) continue;
    const box = underlayVisibleAabb(underlay);
    consider(box.x, box.y);
    consider(box.x + box.w, box.y + box.h);
  }
  (data.measures || []).forEach((measure) => {
    consider(measure.x1, measure.y1);
    consider(measure.x2, measure.y2);
  });
  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY, widthIn: maxX - minX, depthIn: maxY - minY };
}

/** Keep geometry in positive space and grow the drawing sheet if walls run past the edge. */
export function normalizePlanExtents(data: BoardFloorPlanData): BoardFloorPlanData {
  const bounds = contentBounds(data);
  if (!bounds) return data;
  const shiftX = bounds.minX < 0 ? -bounds.minX : 0;
  const shiftY = bounds.minY < 0 ? -bounds.minY : 0;
  const walls =
    shiftX || shiftY
      ? data.walls.map((wall) => ({
          ...wall,
          x1: wall.x1 + shiftX,
          y1: wall.y1 + shiftY,
          x2: wall.x2 + shiftX,
          y2: wall.y2 + shiftY,
        }))
      : data.walls;
  const items =
    shiftX || shiftY
      ? data.items.map((item) => ({ ...item, x: item.x + shiftX, y: item.y + shiftY }))
      : data.items;
  const underlays =
    shiftX || shiftY
      ? (data.underlays || []).map((underlay) => ({
          ...underlay,
          x: underlay.x + shiftX,
          y: underlay.y + shiftY,
        }))
      : data.underlays;
  const measures =
    shiftX || shiftY
      ? (data.measures || []).map((measure) => ({
          ...measure,
          x1: measure.x1 + shiftX,
          y1: measure.y1 + shiftY,
          x2: measure.x2 + shiftX,
          y2: measure.y2 + shiftY,
        }))
      : data.measures;
  const pad = 24;
  return {
    ...data,
    walls,
    items,
    underlays,
    measures,
    footprint: {
      widthIn: Math.max(data.footprint.widthIn + shiftX, bounds.maxX + shiftX + pad, 12 * 8),
      depthIn: Math.max(data.footprint.depthIn + shiftY, bounds.maxY + shiftY + pad, 12 * 8),
    },
  };
}

export function resizeFootprint(
  data: BoardFloorPlanData,
  widthIn: number,
  depthIn: number,
): BoardFloorPlanData {
  const bounds = contentBounds(data);
  const nextW = Math.max(widthIn, bounds?.widthIn ?? 0, 12 * 8);
  const nextD = Math.max(depthIn, bounds?.depthIn ?? 0, 12 * 8);
  return {
    ...data,
    footprint: { widthIn: nextW, depthIn: nextD },
  };
}

export function itemAabb(item: FloorPlanItem): { x: number; y: number; w: number; h: number } {
  const size = rotatedRectSize(item.widthIn, item.depthIn, item.rotation || 0);
  return {
    x: item.x,
    y: item.y,
    w: size.w,
    h: size.h,
  };
}

export function setItemRotation(item: FloorPlanItem, deg: number, snap = false): FloorPlanItem {
  const nextRot = snap ? snapRotationToCardinal(deg) : normalizeDegrees(deg);
  const old = itemAabb(item);
  const next = { ...item, rotation: nextRot };
  const neu = itemAabb(next);
  return {
    ...next,
    x: old.x + old.w / 2 - neu.w / 2,
    y: old.y + old.h / 2 - neu.h / 2,
  };
}

export function rotateItem(item: FloorPlanItem): FloorPlanItem {
  return setItemRotation(item, (item.rotation || 0) + 90);
}

export function hitTestItem(items: FloorPlanItem[], x: number, y: number): FloorPlanItem | null {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const box = itemAabb(items[i]);
    if (x >= box.x && x <= box.x + box.w && y >= box.y && y <= box.y + box.h) {
      return items[i];
    }
  }
  return null;
}

export function hitTestOpening(
  walls: FloorPlanWall[],
  openings: FloorPlanOpening[],
  x: number,
  y: number,
): FloorPlanOpening | null {
  let best: FloorPlanOpening | null = null;
  let bestDist = Infinity;
  for (const opening of openings) {
    const wall = walls.find((w) => w.id === opening.wallId);
    if (!wall) continue;
    const sized = clampOpeningOnWall(wall, opening);
    const hit = projectOnWall(wall, x, y);
    if (hit.rawOffset < sized.offsetIn - 3 || hit.rawOffset > sized.offsetIn + sized.widthIn + 3) {
      continue;
    }
    const half = (wall.thicknessIn || 6) / 2;
    let minSigned = -half - 4;
    let maxSigned = half + 4;
    if (opening.kind === 'garage_door') {
      const layout = garageDoorLayout(wall, opening);
      const reach = layout.half + layout.face + 4;
      const side = opening.flip ? 1 : -1;
      minSigned = Math.min(-layout.half - 3, side * reach);
      maxSigned = Math.max(layout.half + 3, side * reach);
    } else if (opening.kind === 'door') {
      const side = opening.flip ? -1 : 1;
      const reach = opening.widthIn + 4;
      minSigned = Math.min(-half - 3, side * reach);
      maxSigned = Math.max(half + 3, side * reach);
    }
    if (hit.signedDist < minSigned || hit.signedDist > maxSigned) continue;
    if (hit.dist < bestDist) {
      best = opening;
      bestDist = hit.dist;
    }
  }
  return best;
}

export function snapToEndpoints(
  x: number,
  y: number,
  walls: FloorPlanWall[],
  threshold = 8,
): { x: number; y: number } {
  let best = { x, y };
  let bestDist = threshold;
  for (const wall of walls) {
    for (const pt of [
      { x: wall.x1, y: wall.y1 },
      { x: wall.x2, y: wall.y2 },
    ]) {
      const dist = Math.hypot(x - pt.x, y - pt.y);
      if (dist <= bestDist) {
        best = pt;
        bestDist = dist;
      }
    }
  }
  return best;
}

export function snapPlanPoint(
  x: number,
  y: number,
  walls: FloorPlanWall[] = [],
  enabled = true,
): { x: number; y: number } {
  if (!enabled) return { x, y };
  const grid = { x: snapInches(x), y: snapInches(y) };
  return snapToEndpoints(grid.x, grid.y, walls);
}

export function hasEndpointNear(
  walls: FloorPlanWall[],
  x: number,
  y: number,
  threshold = 8,
): boolean {
  for (const wall of walls) {
    if (Math.hypot(x - wall.x1, y - wall.y1) <= threshold) return true;
    if (Math.hypot(x - wall.x2, y - wall.y2) <= threshold) return true;
  }
  return false;
}

export const FP_MIN_ZOOM = 0.1;
export const FP_MAX_ZOOM = 80;
/** Closest view: this many inches of plan across the viewport. */
export const FP_MIN_VIEW_WIDTH_IN = 8;

export function floorPlanZoomLimits(fitted: FloorPlanViewBox): { minW: number; maxW: number } {
  return {
    minW: FP_MIN_VIEW_WIDTH_IN,
    maxW: Math.max(fitted.w / FP_MIN_ZOOM, FP_MIN_VIEW_WIDTH_IN * 8),
  };
}

export function fitViewBox(
  footprint: FloorPlanFootprint,
  svgW: number,
  svgH: number,
  padding = 36,
): FloorPlanViewBox {
  return fitRectViewBox(
    { x: 0, y: 0, w: footprint.widthIn, h: footprint.depthIn },
    svgW,
    svgH,
    padding,
  );
}

export function fitRectViewBox(
  rect: { x: number; y: number; w: number; h: number },
  svgW: number,
  svgH: number,
  padding = 36,
): FloorPlanViewBox {
  const contentW = Math.max(rect.w, 1) + padding * 2;
  const contentH = Math.max(rect.h, 1) + padding * 2;
  const aspect = svgW > 0 && svgH > 0 ? svgW / svgH : contentW / contentH;
  let w = contentW;
  let h = contentH;
  if (aspect > contentW / contentH) {
    w = contentH * aspect;
  } else {
    h = contentW / aspect;
  }
  return {
    x: rect.x + rect.w / 2 - w / 2,
    y: rect.y + rect.h / 2 - h / 2,
    w,
    h,
  };
}

/** Frame walls, items, and visible traces. Empty plans fall back to the sheet. */
export function fitPlanViewBox(
  data: Pick<BoardFloorPlanData, 'footprint' | 'walls' | 'items' | 'underlays'>,
  svgW: number,
  svgH: number,
  padding = 36,
): FloorPlanViewBox {
  const bounds = contentBounds(data);
  if (!bounds) return fitViewBox(data.footprint, svgW, svgH, padding);
  return fitRectViewBox(
    { x: bounds.minX, y: bounds.minY, w: bounds.widthIn, h: bounds.depthIn },
    svgW,
    svgH,
    padding,
  );
}

export function zoomViewBox(
  vb: FloorPlanViewBox,
  factor: number,
  focusX: number,
  focusY: number,
  limits?: { minW: number; maxW: number },
): FloorPlanViewBox {
  let nw = vb.w * factor;
  let nh = vb.h * factor;
  if (limits) {
    if (nw < limits.minW) {
      const scale = limits.minW / nw;
      nw = limits.minW;
      nh *= scale;
    } else if (nw > limits.maxW) {
      const scale = limits.maxW / nw;
      nw = limits.maxW;
      nh *= scale;
    }
  }
  return {
    x: focusX - ((focusX - vb.x) * nw) / vb.w,
    y: focusY - ((focusY - vb.y) * nh) / vb.h,
    w: nw,
    h: nh,
  };
}

export function panViewBox(vb: FloorPlanViewBox, dx: number, dy: number): FloorPlanViewBox {
  return { ...vb, x: vb.x + dx, y: vb.y + dy };
}

/** Same feel as the inspiration board: two-finger scroll pans, ctrl/pinch zooms. */
export function applyFloorPlanWheel(
  vb: FloorPlanViewBox,
  fitted: FloorPlanViewBox,
  opts: {
    deltaX: number;
    deltaY: number;
    deltaMode: number;
    ctrlKey: boolean;
    metaKey?: boolean;
    svgW: number;
    svgH: number;
    focusX: number;
    focusY: number;
    macPinch?: boolean;
  },
): FloorPlanViewBox {
  if (opts.ctrlKey || opts.metaKey) {
    const pinchDelta =
      -opts.deltaY *
      (opts.deltaMode === 1 ? 0.05 : opts.deltaMode ? 1 : 0.002) *
      (opts.macPinch ? 10 : 1);
    return zoomViewBox(vb, Math.pow(2, -pinchDelta), opts.focusX, opts.focusY, floorPlanZoomLimits(fitted));
  }
  const deltaNormalize = opts.deltaMode === 1 ? 20 : 1;
  const dx = ((opts.deltaX * deltaNormalize) / Math.max(opts.svgW, 1)) * vb.w * 0.5;
  const dy = ((opts.deltaY * deltaNormalize) / Math.max(opts.svgH, 1)) * vb.h * 0.5;
  return panViewBox(vb, dx, dy);
}

export function clientPointToInchesFromView(
  viewBox: FloorPlanViewBox,
  rect: { left: number; top: number; width: number; height: number },
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  const w = Math.max(rect.width, 1);
  const h = Math.max(rect.height, 1);
  return {
    x: viewBox.x + ((clientX - rect.left) / w) * viewBox.w,
    y: viewBox.y + ((clientY - rect.top) / h) * viewBox.h,
  };
}

export function clientPointToInches(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
  viewBox?: FloorPlanViewBox,
): { x: number; y: number } | null {
  const ctm = svg.getScreenCTM();
  if (ctm) {
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const mapped = pt.matrixTransform(ctm.inverse());
    return { x: mapped.x, y: mapped.y };
  }
  if (!viewBox) return null;
  return clientPointToInchesFromView(viewBox, svg.getBoundingClientRect(), clientX, clientY);
}

/** Extra gap so square wall caps stop on the jamb instead of overlapping the opening. */
export function openingWallCut(wall: FloorPlanWall, opening: Pick<FloorPlanOpening, 'offsetIn' | 'widthIn'>): {
  start: number;
  end: number;
} {
  const sized = clampOpeningOnWall(wall, opening);
  const cap = (wall.thicknessIn || 6) / 2;
  return { start: sized.offsetIn - cap, end: sized.offsetIn + sized.widthIn + cap };
}

export function wallSegments(
  wall: FloorPlanWall,
  openings: FloorPlanOpening[],
): Array<{ x1: number; y1: number; x2: number; y2: number }> {
  const hosted = openings
    .filter((opening) => opening.wallId === wall.id)
    .map((opening) => clampOpeningOnWall(wall, opening))
    .sort((a, b) => a.offsetIn - b.offsetIn);
  const { length } = wallUnit(wall);
  const cuts: Array<{ start: number; end: number }> = [];
  for (const opening of hosted) {
    cuts.push(openingWallCut(wall, opening));
  }
  const segs: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
  let cursor = 0;
  for (const cut of cuts) {
    if (cut.start > cursor + 0.5) {
      const a = pointAlongWall(wall, cursor);
      const b = pointAlongWall(wall, cut.start);
      segs.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
    }
    cursor = Math.max(cursor, cut.end);
  }
  if (cursor < length - 0.5) {
    const a = pointAlongWall(wall, cursor);
    const b = pointAlongWall(wall, length);
    segs.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
  }
  return segs;
}

export function clonePlan(data: BoardFloorPlanData): BoardFloorPlanData {
  return {
    title: data.title,
    footprint: { ...data.footprint },
    walls: data.walls.map((wall) => ({ ...wall })),
    openings: data.openings.map((opening) => ({ ...opening })),
    items: data.items.map((item) => ({ ...item, stair: item.stair ? { ...item.stair } : item.stair })),
    underlays: (data.underlays || []).map((underlay) => ({
      ...underlay,
      crop: underlay.crop ? { ...underlay.crop } : underlay.crop,
      locked: underlay.locked,
      hidden: underlay.hidden,
    })),
    constraints: data.constraints?.map((constraint) => ({ ...constraint })),
    measures: data.measures?.map((measure) => ({ ...measure })),
  };
}
